/**
 * GraphQL queries and hooks — maturityAssessment pillar.
 * Split from graphql/queries.ts (issue #1409).
 */

import { gql } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';
import { useAllPages } from './useAllPages';
import type {
  MaturityAssessment,
  MaturityAssessmentConnection,
  MaturityAssessmentFilter,
  MaturityFramework,
  MaturityFrameworkConnection,
} from '../types/maturityAssessment';

const MATURITY_FRAMEWORK_FIELDS = gql`
  fragment MaturityFrameworkFields on MaturityFramework {
    entityId
    entityType
    name
    description
    version
    status
    effectiveFrom
    effectiveTo
    areas {
      id
      name
      order
      categories {
        id
        name
        order
        capabilities {
          id
          name
          order
          levels {
            level
            name
            description
            criteria
          }
        }
      }
    }
    _schemaVersion
    _createdAt
    _createdBy
    _updatedAt
    _updatedBy
  }
`;

const MATURITY_ASSESSMENT_FIELDS = gql`
  fragment MaturityAssessmentFields on MaturityAssessment {
    entityId
    entityType
    name
    description
    frameworkId
    frameworkVersion
    pollId
    subjectEntityType
    subjectEntityId
    assessmentPeriod
    assessmentDate
    dueDate
    assessorUserId
    overallScore
    areaScores {
      areaId
      score
    }
    categoryScores {
      categoryId
      score
    }
    responses {
      capabilityId
      level
      comment
      respondedAt
    }
    status
    completionPercentage
    approvedBy
    approvedAt
    ownerType
    ownerId
    _schemaVersion
    _createdAt
    _createdBy
    _updatedAt
    _updatedBy
  }
`;

export const GET_MATURITY_FRAMEWORK = gql`
  ${MATURITY_FRAMEWORK_FIELDS}
  query GetMaturityFramework($id: ID!) {
    maturityFramework(id: $id) {
      ...MaturityFrameworkFields
    }
  }
`;

export const LIST_MATURITY_FRAMEWORKS = gql`
  ${MATURITY_FRAMEWORK_FIELDS}
  query ListMaturityFrameworks($first: Int, $after: String) {
    maturityFrameworks(first: $first, after: $after) {
      edges {
        node {
          ...MaturityFrameworkFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

export const GET_MATURITY_ASSESSMENT = gql`
  ${MATURITY_ASSESSMENT_FIELDS}
  query GetMaturityAssessment($id: ID!) {
    maturityAssessment(id: $id) {
      ...MaturityAssessmentFields
    }
  }
`;

export const LIST_MATURITY_ASSESSMENTS = gql`
  ${MATURITY_ASSESSMENT_FIELDS}
  query ListMaturityAssessments($filter: MaturityAssessmentFilter, $first: Int, $after: String) {
    maturityAssessments(filter: $filter, first: $first, after: $after) {
      edges {
        node {
          ...MaturityAssessmentFields
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
`;

export function useMaturityFramework(
  id: string
): QueryResult<{ maturityFramework: MaturityFramework | null }> {
  return useQuery(GET_MATURITY_FRAMEWORK, {
    variables: { id },
    skip: !id,
  });
}

export function useMaturityFrameworks(
  first: number = 50,
  skip: boolean = false
): QueryResult<{ maturityFrameworks: MaturityFrameworkConnection }> {
  return useQuery(LIST_MATURITY_FRAMEWORKS, {
    variables: { first },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    skip,
  });
}

export function useMaturityAssessment(
  id: string
): QueryResult<{ maturityAssessment: MaturityAssessment | null }> {
  return useQuery(GET_MATURITY_ASSESSMENT, {
    variables: { id },
    skip: !id,
  });
}

export function useMaturityAssessments(
  filter?: MaturityAssessmentFilter,
  first: number = 50,
  skip: boolean = false
): QueryResult<{ maturityAssessments: MaturityAssessmentConnection }> {
  return useQuery(LIST_MATURITY_ASSESSMENTS, {
    variables: { filter, first },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    skip,
  });
}

/**
 * Fetch every page of `maturityAssessments` for `filter` (see {@link useAllPages}).
 *
 * Used where the full set must be inspected for correctness — e.g. discovering
 * every distinct `frameworkId` a subject has been assessed under. A capped
 * single-page fetch would silently miss a second framework whose rows sort
 * past the first page, hiding both the framework selector and that
 * framework's history (issue #1603 review, mirrored for Team subjects by
 * issue #1618).
 */
export function useAllMaturityAssessments(
  filter?: MaturityAssessmentFilter,
  first: number = 50
): QueryResult<{ maturityAssessments: MaturityAssessmentConnection }> {
  return useAllPages<{ maturityAssessments: MaturityAssessmentConnection }>(
    LIST_MATURITY_ASSESSMENTS,
    (data) => data?.maturityAssessments,
    { filter },
    first
  );
}

// ─── Maturity Trend / Comparison / Distribution ───────────────────────────
